import { number, object, string } from 'yup';
import { tableSortFilter } from '@/utils/arrayProcessing';
import { ProviderCityPrice, ProviderCityPriceServiceVariant } from '../types';

export const PROVIDER_CITY_PRICE_TABLE_HEAD = [
  { id: 'emptyCell', label: '', align: 'left' },
  { id: 'cities.name', label: 'City', align: 'left' },
  { id: 'service_variant.service.name', label: 'Service Type', align: 'left' },
  { id: 'service_variant.name', label: 'Service Variant', align: 'left' },
  { id: 'price', label: 'Price', align: 'left' },
  { id: 'sun_bank_holiday_extra', label: 'Sunday/BH extra', align: 'left' },
  { id: 'start_date', label: 'Start date', align: 'left' },
  { id: 'end_date', label: 'End date', align: 'left' },
  { id: '3dotMenu', label: '', align: 'right' },
];

export const PROVIDER_CITY_PRICE_DEFAULT_VALUES = {
  city_id: '',
  service_type_id: '',
  service_variant_id: '',
  price: '',
  sun_bank_holiday_extra: '',
  start_date: '',
  end_date: '',
};

export const PROVIDER_CITY_PRICE_SCHEMA = object().shape({
  city_id: string().required('City is required'),
  service_variant_id: string().required('Service Variant is required'),
  price: number().typeError('Price is required').required('Price is required'),
  sun_bank_holiday_extra: number()
    .typeError('Extra price is required')
    .required('Extra price is required'),
  start_date: string().required('Start date is required'),
  end_date: string()
    .required('End Date is required')
    .test(
      'is-greater',
      'End Date must be greater than Start Date',
      function (end_date) {
        const { start_date } = this.parent;
        return end_date && start_date
          ? new Date(end_date) > new Date(start_date)
          : true;
      }
    ),
});

const dateAtStartOfDay = (value: string | Date) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getProviderCityPriceCityName = (price: ProviderCityPrice) =>
  price.cities?.name ?? price.city?.name ?? 'N/A';

export const getProviderCityPriceServiceName = (price: ProviderCityPrice) =>
  price.service_variant?.service?.name ?? 'N/A';

export const getProviderCityPriceVariantName = (price: ProviderCityPrice) =>
  getServiceVariantName(price.service_variant);

export const getServiceVariantName = (
  variant:
    | (Partial<ProviderCityPriceServiceVariant> & { name?: string })
    | undefined
) => variant?.variant_name ?? variant?.name ?? 'N/A';

export const isServiceVariantActive = (
  variant: { active?: boolean | number | string } | undefined
) =>
  variant?.active === true ||
  variant?.active === 1 ||
  String(variant?.active).toLowerCase() === 'true' ||
  String(variant?.active).toLowerCase() === 'active';

export const isProviderCityPriceExpired = (price: ProviderCityPrice) =>
  !!price.end_date &&
  dateAtStartOfDay(price.end_date) < dateAtStartOfDay(new Date());

export const isProviderCityPriceActiveOn = (
  price: ProviderCityPrice,
  activeOn: string
) => {
  if (!activeOn) return true;

  const activeOnDate = dateAtStartOfDay(activeOn);
  const startDate = dateAtStartOfDay(price.start_date);
  const endDate = price.end_date ? dateAtStartOfDay(price.end_date) : null;

  return startDate <= activeOnDate && (!endDate || endDate >= activeOnDate);
};

export function applySortFilterProviderCityPrices({
  tableData,
  comparator,
  searchText,
  activeOn,
}: {
  tableData: ProviderCityPrice[];
  comparator: (a: any, b: any) => number;
  searchText: string;
  activeOn: string;
}) {
  tableData = tableSortFilter(tableData, comparator);

  if (searchText) {
    const normalizedSearchText = searchText.toLowerCase();
    tableData = tableData.filter((price) =>
      [
        getProviderCityPriceCityName(price),
        getProviderCityPriceServiceName(price),
        getProviderCityPriceVariantName(price),
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearchText)
    );
  }

  if (activeOn) {
    tableData = tableData.filter((price) =>
      isProviderCityPriceActiveOn(price, activeOn)
    );
  }

  return tableData;
}
